import { FC, useContext, useEffect } from 'react';
import {
 Alert, Box, Divider, FormControl, FormControlLabel, Radio, RadioGroup, Typography
} from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import SocketContext, { uploadFiles } from 'socket/DataSettingsSocket/context';
import {
	dataSettings,
	getSingleReceiptStatusSelector,
	getStatusSingleReceipt,
} from 'store/data-settings';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { UploadFile } from 'components/modules/UploadFile';
import { UtilityMapProcessingWarning } from 'components/modules/UtilityMapProcessingWarning';
import { IUtilityMapStatus } from 'types';
import { UPLOAD_PROVIDERS, UploadField } from 'types/upload-status';
import { FILE_FORMAT } from 'utils/enums';
import { checkStatusFile, checkStatusOption, isSomeProcessInProgress, isUtilityMapProcessRunning } from "utils/helpers";
import { initialValues, IParserForm, RadioInputOptions, validationSchema } from './config';

interface SettingsFormProps {
	isUtilityMapResetting: boolean;
	onUndefinedDataProcessStart: () => void;
	onUtilityMapReset: () => Promise<void>;
	utilityMapStatus: IUtilityMapStatus | null;
}

export const SettingsForm: FC<SettingsFormProps> = ({
	isUtilityMapResetting,
	onUndefinedDataProcessStart,
	onUtilityMapReset,
	utilityMapStatus,
}) => {
	const singleReceiptStatus = useAppSelector(getSingleReceiptStatusSelector);
	const { processesStatus, isConnected, hasActiveUploads, hasIncompleteUploads, startUpload } = useContext(SocketContext);
	const isBusy = !isConnected || hasActiveUploads || isSomeProcessInProgress(processesStatus);
	const dispatch = useAppDispatch();

	const handleSubmit = async (values: IParserForm, { setFieldValue }: FormikHelpers<IParserForm>) => {
		if (isBusy || (values.radioValue === RadioInputOptions.BILL_CREATE_SINGLE_RECEIPT && hasIncompleteUploads)) return;
		if (
			values.radioValue === RadioInputOptions.ETL_UNDEFINED_DATA &&
			(!utilityMapStatus || isUtilityMapProcessRunning(utilityMapStatus))
		) {
			return;
		}

		if (values.radioValue === RadioInputOptions.ETL_FILES_PARSING) {
			await Promise.all((Object.keys(UPLOAD_PROVIDERS) as UploadField[]).map(async (field) => {
				const file = values[field];
				if (file && await startUpload(field, file)) await setFieldValue(field, null, false);
			}));
			return;
		}
		const result = await dispatch(dataSettings(values));

		if (values.radioValue === RadioInputOptions.ETL_UNDEFINED_DATA && dataSettings.fulfilled.match(result)) {
			onUndefinedDataProcessStart();
		}
	};

	
	useEffect(() => {
		dispatch(getStatusSingleReceipt());
	}, [dispatch, singleReceiptStatus]);

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
			{({ errors, handleChange, values }) => (
				<Form id="data">
					<Box>
						<FormControl component="fieldset" disabled={isBusy}>
							<RadioGroup
								aria-label="config-settings"
								name="radioValue"
								value={ values.radioValue }
								sx={{ display: 'flex', gap: '4px' }}
								onChange={(e: any) => {
									handleChange(e);
								}}
							>
								<FormControlLabel
									value={RadioInputOptions.ETL_FILES_PARSING}
									control={<Radio />}
									label="Запустити парсинг файлів"
								/>
								{/*<Box sx={{ display: 'flex', gap: '24px', alignItems: 'center' }}>*/}
								{/*	<Typography variant="body2" color={checkStatusAllObj(processesStatus) ? 'text.disabled' : 'text.primary'}>*/}
								{/*		Мануальний парсинг*/}
								{/*	</Typography>*/}
								{/*	/!*<Switch checked={false} disabled={values.radioValue !== RadioInputOptions.ETL_FILES_PARSING} />*!/*/}
								{/*	<Typography variant="body2" color={checkStatusAllObj(processesStatus) ? 'text.disabled' : 'text.primary'}>*/}
								{/*		Автоматичний парсинг*/}
								{/*	</Typography>*/}
								{/*</Box>*/}
								<UploadFile
									fileFormat={FILE_FORMAT.DBF}
									fieldName="kvbo"
									title="Постачальник КВБО"
									isDisabled={isBusy || checkStatusFile(processesStatus, uploadFiles.kvbo)}
								/>
								<UploadFile
									fileFormat={FILE_FORMAT.TXT}
									fieldName="hvd"
									title="Постачальник ХВД"
									isDisabled={isBusy || checkStatusFile(processesStatus, uploadFiles.kvd)}
								/>
								<UploadFile
									fileFormat={FILE_FORMAT.DBF}
									fieldName="hts"
									title="Постачальник ХТС"
									isDisabled={isBusy || checkStatusFile(processesStatus, uploadFiles.kts)}
								/>
								<UploadFile
									fileFormat={FILE_FORMAT.CSV}
									fieldName="jks"
									title="Постачальник ЖКС"
									isDisabled={isBusy || checkStatusFile(processesStatus, uploadFiles.hcs)}
								/>
								<UploadFile
									fileFormat={FILE_FORMAT.TXT}
									fieldName="hez"
									title="Постачальник ХЕЗ"
									isDisabled={isBusy || checkStatusFile(processesStatus, uploadFiles.hez)}
								/>
								<Divider />
								<FormControlLabel
									value={RadioInputOptions.ETL_UNDEFINED_DATA}
									control={<Radio />}
									label="Необроблені дані"
									disabled={!utilityMapStatus || isUtilityMapProcessRunning(utilityMapStatus)}
								/>
								<UtilityMapProcessingWarning
									isResetting={isUtilityMapResetting}
									status={utilityMapStatus}
									onReset={onUtilityMapReset}
								/>
								<Divider />
								<FormControlLabel
									value={RadioInputOptions.BILL_CREATE_SINGLE_RECEIPT}
									control={<Radio />}
									label="Відправка даних КП на Біл" // TODO: etlMapAndSendToBill
									disabled={isBusy || hasIncompleteUploads || checkStatusOption(processesStatus?.etlMapAndSendToBill)}
								/>
								{hasIncompleteUploads && <Alert severity="error">Дані провайдерів неповні або прийом файла не підтверджено. Відправку в білл заблоковано. Перевірте статуси файлів.</Alert>}
								<Divider />
								<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
									<FormControlLabel
										value={RadioInputOptions.REPORT_CREATE_GENERATE_PDF_SINGLE_RECEIPT}
										control={<Radio />}
										disabled
										// disabled={isSomeProcessInProgress(processesStatus) || checkStatusOption(processesStatus?.billSingleReceiptsSendToReport)}
										label="Відправка ЄК на репорт" // TODO: billSingleReceiptsSendToReport
									/>
								</Box>
								<Divider />
							</RadioGroup>
						</FormControl>
					</Box>
					{errors.radioValue && (
						<Typography variant="body2" color="secondary">
							{errors.radioValue}
						</Typography>
					)}
				</Form>
			)}
		</Formik>
	);
};
